import { getExplorerPane } from '@disco/state-management/selectors/explorer'
import { debounce } from 'lodash'
import React, { useState, useEffect, useCallback } from 'react'
import { DEFAULT_VIEW, paneViewTypes } from '@disco/constants/ViewTypes'

import { useDispatch, useSelector } from 'hooks/use-redux'
import type { Group } from 'types/group'
import { Container, SearchTextField } from 'components/ds'
import { switchPaneView } from '@disco/state-management/actions/ExplorerActions'
import { getIsSubfolder } from '@disco/state-management/selectors/groups'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'

type Props = {
  channel: Group
}

export const InlineChannelSearch = ({ channel }: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const [searchTerm, setSearchTerm] = useState('')

  const selectedPane = useSelector(state =>
    getExplorerPane(state, { index: 0 }),
  )

  const isSubfolder = useSelector(state =>
    getIsSubfolder(state, { groupId: channel.id }),
  )

  const pane = channel.panes.some(p => p.groupId === selectedPane.groupId)
    ? selectedPane
    : null

  const paneView =
    paneViewTypes?.find(view => view === pane?.view) ?? DEFAULT_VIEW

  const debouncedQuery = useCallback(
    debounce(input => {
      if (pane) {
        dispatch(switchPaneView(pane, paneView, { searchTerm: input }))
        dispatch(
          sendMixpanelEvent('Search', {
            Context: 'Channel',
          }),
        )
      }
    }, 300),
    [pane],
  )

  const handleInputChange = (input: string) => {
    setSearchTerm(input)
    debouncedQuery(input)
  }

  useEffect(() => {
    setSearchTerm('')
  }, [pane?.groupId])

  if (!channel) return

  return (
    <Container align="center">
      <SearchTextField
        value={searchTerm}
        type="text"
        size="2"
        onChange={e => handleInputChange(e.target.value)}
        placeholder={`Search in ${
          channel.parent ? (isSubfolder ? 'subfolder' : 'folder') : 'channel'
        }...`}
      />
    </Container>
  )
}
